/**
 * HomeAgentHero — the composer at the centre of the platform home page.
 *
 * Home stops being a wall of widgets and becomes "ask, then look": the first
 * thing on the page is a prompt to the agent team. Submitting hands the text
 * to the agent rail, which picks the right agent for what was asked and keeps
 * the conversation as the user navigates away.
 *
 * The account-manager block is rendered through `accountManagerSlot` so the
 * human contact route stays one glance away from the agent one.
 */
import { useState, type FormEvent, type ReactNode } from 'react';
import { ArrowUp, Sparkles } from 'lucide-react';
import { cn } from '@/components/ui/utils';
import { useAgentRail } from '@/cowork/rail/AgentRailContext';

/** Openers that map onto the Beatly flow rather than generic assistant chatter. */
const SUGGESTIONS = [
  { label: 'Find creators', prompt: 'Find me 20 new creators that fit our brand.' },
  { label: 'Start a campaign', prompt: 'Set up a new campaign brief and take it to a creator list.' },
  { label: "What needs me?", prompt: 'What is waiting on my approval right now?' },
  { label: 'How did we do?', prompt: 'Summarise how our last campaign performed.' },
];

interface HomeAgentHeroProps {
  greeting: string;
  userName: string;
  subtitle: ReactNode;
  accountManagerSlot?: ReactNode;
}

export function HomeAgentHero({
  greeting,
  userName,
  subtitle,
  accountManagerSlot,
}: HomeAgentHeroProps) {
  const { ask } = useAgentRail();
  const [value, setValue] = useState('');

  const submit = (e?: FormEvent) => {
    e?.preventDefault();
    const text = value.trim();
    if (!text) return;
    setValue('');
    ask(text);
  };

  return (
    <div className="relative overflow-hidden rounded-[24px] border-0 bg-gradient-to-br from-[#2e024c] to-[#5a1a8a] shadow-lg">
      <div
        className="pointer-events-none absolute inset-0 opacity-10"
        style={{
          backgroundImage:
            'radial-gradient(circle at 80% 20%, #be43ff 0%, transparent 50%), radial-gradient(circle at 20% 80%, #a9f600 0%, transparent 50%)',
        }}
      />

      {accountManagerSlot && (
        <div className="absolute right-5 top-5 z-20 hidden lg:block">{accountManagerSlot}</div>
      )}

      <div className="relative z-10 mx-auto flex w-full max-w-2xl flex-col items-center px-5 py-10 text-center md:py-14">
        <span className="text-sm text-[#a9f600]">{greeting},</span>
        <h1 className="mt-1 text-[28px] text-white lg:text-[36px]" style={{ fontWeight: 700 }}>
          Welcome, <span className="text-[#be43ff]">{userName}</span>
        </h1>
        <p className="mt-2 max-w-lg text-sm text-white/60">{subtitle}</p>

        <form onSubmit={submit} className="mt-7 w-full">
          <div className="flex items-center gap-2 rounded-2xl bg-white/95 p-2 pl-4 shadow-xl ring-1 ring-white/20 backdrop-blur focus-within:ring-2 focus-within:ring-[#be43ff]">
            <Sparkles className="h-4 w-4 shrink-0 text-[#be43ff]" aria-hidden />
            <input
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  submit();
                }
              }}
              placeholder="Ask your agents to do something…"
              aria-label="Ask your agents"
              className="min-w-0 flex-1 bg-transparent py-2.5 text-sm text-[#101828] outline-none placeholder:text-[#98a2b3]"
            />
            <button
              type="submit"
              disabled={!value.trim()}
              aria-label="Send to your agents"
              className={cn(
                'flex h-9 w-9 shrink-0 items-center justify-center rounded-xl transition-colors',
                value.trim()
                  ? 'bg-[#be43ff] text-white hover:bg-[#a935e6]'
                  : 'bg-[#f2f4f7] text-[#d0d5dd]',
              )}
            >
              <ArrowUp className="h-4 w-4" aria-hidden />
            </button>
          </div>
        </form>

        <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
          {SUGGESTIONS.map((s) => (
            <button
              key={s.label}
              type="button"
              onClick={() => ask(s.prompt)}
              className="rounded-full border border-white/20 bg-white/10 px-3.5 py-1.5 text-xs text-white/80 transition-colors hover:bg-white/20 hover:text-white"
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
